import { useState } from 'react';
import { Icon } from '../../../layout/Icon.jsx';
import { useImportProducts } from '../hooks/useProductMutations.js';

function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return [];
  const headers = splitCsvLine(lines[0]);
  return lines.slice(1).map((line) => {
    const cells = splitCsvLine(line);
    const obj = {};
    headers.forEach((h, i) => { obj[h.trim()] = (cells[i] ?? '').trim(); });
    return obj;
  });
}

function splitCsvLine(line) {
  const out = [];
  let cur = '';
  let q = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (q) {
      if (ch === '"' && line[i + 1] === '"') { cur += '"'; i += 1; }
      else if (ch === '"') q = false;
      else cur += ch;
    } else {
      if (ch === ',') { out.push(cur); cur = ''; }
      else if (ch === '"') q = true;
      else cur += ch;
    }
  }
  out.push(cur);
  return out;
}

function coerceItems(rows, warehouses, categories) {
  const whByName = new Map(warehouses.map((w) => [String(w.name).trim(), w._id || w.id]));
  const whByKey = new Map(warehouses.map((w) => [String(w.key || '').trim(), w._id || w.id]));
  const catByName = new Map(categories.map((c) => [`${c.warehouseId?._id || c.warehouseId}|${String(c.name).trim()}`, c._id || c.id]));

  return rows.map((r) => {
    const out = { ...r };
    if (out.warehouse && !out.warehouseId) {
      out.warehouseId = whByName.get(String(out.warehouse).trim()) || whByKey.get(String(out.warehouse).trim());
    }
    if (out.category && out.warehouseId && !out.categoryId) {
      out.categoryId = catByName.get(`${out.warehouseId}|${String(out.category).trim()}`);
    }
    if (out.costPrice != null) out.costPrice = Number(out.costPrice) || 0;
    if (out.salePrice != null) out.salePrice = Number(out.salePrice) || 0;
    if (out.minStockAlert != null) out.minStockAlert = Number(out.minStockAlert) || 0;
    if (out.isActive != null) out.isActive = String(out.isActive).toLowerCase() !== 'false';
    delete out.warehouse;
    delete out.category;
    delete out.currentStock;
    return out;
  });
}

export function BulkImportModal({ open, onClose, warehouses = [], categories = [] }) {
  const [text, setText] = useState('');
  const [mode, setMode] = useState('json');
  const [result, setResult] = useState(null);
  const importMutation = useImportProducts();

  if (!open) return null;

  async function onFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setText(String(reader.result || ''));
      setMode(file.name.toLowerCase().endsWith('.csv') ? 'csv' : 'json');
    };
    reader.readAsText(file, 'utf-8');
  }

  async function submit() {
    setResult(null);
    let rows = [];
    try {
      if (mode === 'json') {
        const parsed = JSON.parse(text);
        rows = Array.isArray(parsed) ? parsed : (parsed.items || []);
      } else {
        rows = parseCSV(text);
        rows = coerceItems(rows, warehouses, categories);
      }
    } catch (err) {
      setResult({ error: 'تعذّر قراءة البيانات: ' + err.message });
      return;
    }
    if (!rows.length) {
      setResult({ error: 'لا توجد سطور صالحة' });
      return;
    }
    try {
      const res = await importMutation.mutateAsync(rows);
      setResult({ ...res, total: rows.length });
    } catch (err) {
      setResult({ error: err?.response?.data?.error?.message || err.message });
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center p-4"
      style={{ background: 'rgba(0,0,0,.45)' }}
    >
      <div className="card w-[640px] max-w-full max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="card-h">
          <h3 className="text-[15px] font-bold flex-1">استيراد منتجات</h3>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            <Icon name="x" size={14} />
          </button>
        </div>
        <div className="card-b flex-1 overflow-y-auto space-y-3">
          <div className="inline-flex gap-1 p-1 bg-paper-2 rounded-[10px]">
            <button
              type="button"
              onClick={() => setMode('json')}
              className={mode === 'json' ? 'btn btn-primary' : 'btn'}
              style={{ padding: '6px 12px', fontSize: 12 }}
            >
              JSON
            </button>
            <button
              type="button"
              onClick={() => setMode('csv')}
              className={mode === 'csv' ? 'btn btn-primary' : 'btn'}
              style={{ padding: '6px 12px', fontSize: 12 }}
            >
              CSV
            </button>
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">رفع ملف</label>
            <input type="file" accept=".csv,.json,application/json,text/csv" onChange={onFile} />
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">
              أو الصق المحتوى ({mode === 'json' ? 'مصفوفة JSON' : 'CSV بترويسة'})
            </label>
            <textarea
              className="input"
              rows={8}
              dir="ltr"
              style={{ fontFamily: 'monospace', textAlign: 'left' }}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={mode === 'json'
                ? '[{"name":"كولا","barcode":"...","warehouseId":"...","categoryId":"...","costPrice":500,"salePrice":1000}]'
                : 'name,barcode,warehouse,category,costPrice,salePrice,minStockAlert\nكولا,620...,كافتيريا,مشروبات,500,1000,5'}
            />
          </div>

          {mode === 'csv' ? (
            <div className="bg-paper-2 rounded-[10px] p-2.5 text-[11.5px] text-ink-2">
              ملاحظة: الأعمدة المدعومة — name, barcode, warehouse (اسم المخزن), category (اسم القسم),
              costPrice, salePrice, minStockAlert, type, unit, isActive. سيتم استبدال أسماء المخزن والقسم بمعرّفاتها.
            </div>
          ) : null}

          {result ? (
            result.error ? (
              <div className="bg-[#FEE2E2] text-[#B91C1C] rounded-[10px] p-3 text-[12.5px] font-semibold">
                {result.error}
              </div>
            ) : (
              <div className="bg-[#DCFCE7] text-[#15803D] rounded-[10px] p-3 text-[12.5px]">
                <div className="font-bold mb-1">تم الاستيراد</div>
                <div>إجمالي السطور: {result.total}</div>
                <div>تم الإنشاء: {result.created}</div>
                <div>تم التخطّي (مكرر): {result.skipped}</div>
                {result.errors?.length ? (
                  <details className="mt-1.5">
                    <summary className="cursor-pointer">{result.errors.length} خطأ</summary>
                    <ul className="mt-1 list-disc ps-4 text-[11px]">
                      {result.errors.slice(0, 20).map((e, i) => (
                        <li key={i}>سطر {e.index + 1}: {e.message}</li>
                      ))}
                    </ul>
                  </details>
                ) : null}
              </div>
            )
          ) : null}
        </div>
        <div className="card-h border-b-0 border-t flex justify-end gap-2">
          <button type="button" className="btn" onClick={onClose}>إغلاق</button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={submit}
            disabled={importMutation.isPending || !text.trim()}
          >
            {importMutation.isPending ? 'جاري الاستيراد...' : 'استيراد'}
          </button>
        </div>
      </div>
    </div>
  );
}
